import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { formatDate, formatPrice } from "@/lib/formatters";
import { NormalizedDataPoint } from "@/lib/PreparedDataset";
import { XIcon } from "lucide-react";

type OutlierManagementProps = {
  outlierProperties: NormalizedDataPoint[];
  onAutoExcludeClick: () => void;
  onRemoveOutlier: (id: string) => Promise<void>;
};

export function OutlierManagement({
  outlierProperties,
  onAutoExcludeClick,
  onRemoveOutlier,
}: OutlierManagementProps) {
  return (
    <div>
      <div className="font-bold pb-2">Remove outliers and bad data</div>

      <div className="space-y-4">
        <div className="space-y-2">
          <p className="text-sm">Click any point on the chart to exclude from your analysis.</p>
          <p className="text-sm">
            Click the button below to automatically exclude statistical outliers.
          </p>
          <p className="text-sm">
            <Button
              variant="outline"
              onClick={onAutoExcludeClick}
              data-testid="auto-remove-outliers-btn"
            >
              Remove outliers
            </Button>
          </p>
          <p className="text-sm">
            The dashed lines on the chart display 2 standard deviations from the mean. It is common
            to use this as a threshold for defining outliers.
          </p>
        </div>

        {outlierProperties.length > 0 && (
          <>
            <p className="font-bold" data-testid="outlier-count">
              {outlierProperties.length === 1
                ? "Excluded point (1)"
                : `Excluded points (${outlierProperties.length})`}
            </p>

            <Table data-testid="outlier-table">
              <TableHeader>
                <TableRow>
                  <TableHead>Address</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Price</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {outlierProperties.map((o) => (
                  <TableRow key={o.id}>
                    <TableCell className="p-2 text-sm">{o.address}</TableCell>
                    <TableCell className="p-2 text-sm">{formatDate(o.date.getTime())}</TableCell>
                    <TableCell className="p-2 text-sm">{formatPrice(o.price)}</TableCell>
                    <TableCell className="p-2 text-sm">
                      <Button
                        variant="ghost"
                        size="xs"
                        className="flex -m-1 justify-center"
                        onClick={async () => {
                          await onRemoveOutlier(o.id);
                        }}
                        data-testid="remove-outlier-btn"
                      >
                        <XIcon className="w-4 h-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </>
        )}
      </div>
    </div>
  );
}
